/**
 * Socket Event DTOs for Client-Server API Proxy Communication
 * 
 * This file defines all the request/response interfaces for socket-based
 * communication between the Angular client and WOPI server.
 */

/**
 * Base request interface with unique ID for tracking
 */
export interface BaseSocketRequest {
  requestId: string;
  timestamp: number;
}

/**
 * Base response interface
 */
export interface BaseSocketResponse {
  requestId: string;
  success: boolean;
  error?: string;
  timestamp: number;
}

/**
 * Get File Entity Request
 */
export interface GetFileEntityRequest extends BaseSocketRequest {
  bizEntityId: string;
  clientHeaders: {
    authorization?: string;
    appuuid?: string;
    tenantuuid?: string;
  };
}

/**
 * Get File Entity Response
 */
export interface GetFileEntityResponse extends BaseSocketResponse {
  data?: {
    id: string;
    name: string;
    fileUrl: string | string[];
  } | null;
}

/**
 * Get Biz Entity Request
 */
export interface GetBizEntityRequest extends BaseSocketRequest {
  bizEntityId: string;
  clientHeaders: {
    authorization?: string;
    appuuid?: string;
    tenantuuid?: string;
  };
}

/**
 * Get Biz Entity Response
 */
export interface GetBizEntityResponse extends BaseSocketResponse {
  data?: {
    id: string;
    name: string;
    additionalInfo: any[];
  } | null;
}

/**
 * Upload File Request
 */
export interface UploadFileRequest extends BaseSocketRequest {
  bizEntityId: string;
  fileName: string;
  fileData: string; // Base64 encoded file data
  fileSize: number;
  thumbnail?: string | undefined; // Base64 encoded thumbnail data
  additionalHeaders?: Record<string, string>;
  clientHeaders: {
    authorization?: string;
    appuuid?: string;
    tenantuuid?: string;
  };
}

/**
 * Upload File Response
 */
export interface UploadFileResponse extends BaseSocketResponse {
  data?: {
    fileUrl?: string | string[];
    url?: string;
    location?: string;
  };
}

/**
 * Download File Request
 */
export interface DownloadFileRequest extends BaseSocketRequest {
  fileUrl: string;
  bizEntityId?: string; // File ID for room-based routing
}

/**
 * Download File Response
 */
export interface DownloadFileResponse extends BaseSocketResponse {
  data?: string; // Base64 encoded file data
  size?: number;
}

/**
 * Get File Metadata Request (HEAD request)
 */
export interface GetFileMetadataRequest extends BaseSocketRequest {
  fileUrl: string;
  bizEntityId?: string; // For room-based routing
}

/**
 * Get File Metadata Response
 */
export interface GetFileMetadataResponse extends BaseSocketResponse {
  data?: {
    size: number;
    lastModified: string;
  };
}

/**
 * Join File Room Request
 */
export interface JoinFileRoomRequest {
  fileId: string;
  userId: string;
  tenantId?: string;
}

/**
 * Leave File Room Request
 */
export interface LeaveFileRoomRequest {
  fileId: string;
  userId: string;
}

/**
 * File Room Joined Response
 */
export interface FileRoomJoinedResponse {
  fileId: string;
  success: boolean;
  timestamp: number;
}

/**
 * Socket Event Names
 */
export enum SocketEvents {
  // Client -> Server (Request)
  GET_FILE_ENTITY = 'api:getFileEntity',
  GET_BIZ_ENTITY = 'api:getBizEntity',
  UPLOAD_FILE = 'api:uploadFile',
  DOWNLOAD_FILE = 'api:downloadFile',
  GET_FILE_METADATA = 'api:getFileMetadata',

  // Server -> Client (Response)
  GET_FILE_ENTITY_RESPONSE = 'api:getFileEntity:response',
  GET_BIZ_ENTITY_RESPONSE = 'api:getBizEntity:response',
  UPLOAD_FILE_RESPONSE = 'api:uploadFile:response',
  DOWNLOAD_FILE_RESPONSE = 'api:downloadFile:response',
  GET_FILE_METADATA_RESPONSE = 'api:getFileMetadata:response',

  // Connection events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',
  RECONNECT = 'reconnect',
  RECONNECT_ATTEMPT = 'reconnect_attempt',
  RECONNECT_ERROR = 'reconnect_error',
  RECONNECT_FAILED = 'reconnect_failed',

  // File room management (for file-specific isolation)
  FILE_JOIN_ROOM = 'file:joinRoom',
  FILE_LEAVE_ROOM = 'file:leaveRoom',
  FILE_ROOM_JOINED = 'file:roomJoined',
  FILE_ROOM_LEFT = 'file:roomLeft',
  FILE_ROOM_ERROR = 'file:roomError',

  // Connection management
  CONNECTION_READY = 'connection:ready',
  HEARTBEAT = 'heartbeat',
  HEARTBEAT_ACK = 'heartbeat:ack',

  // Error handling
  REQUEST_TIMEOUT = 'request:timeout',
  REQUEST_ERROR = 'request:error',
}

/**
 * Socket configuration constants
 */
export const SOCKET_CONFIG = {
  REQUEST_TIMEOUT: 30000, // 30 seconds
  HEARTBEAT_INTERVAL: 5000, // 5 seconds
  RECONNECTION_ATTEMPTS: 5,
  RECONNECTION_DELAY: 2000, // 2 seconds
  MAX_PAYLOAD_SIZE: 50 * 1024 * 1024, // 50 MB
  DEFAULT_WOPI_URL: 'https://dev.optimastride.com',
};
